import {useEffect, useRef, useState} from 'react';
import {ApiError, approveCustomerTestQuote, authorizeCustomerMockPayment, CustomerTestQuote, loadCustomerTestQuotes} from './api';

const money = (amount: number, currency: string) => new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(amount / 100);

function readableQuote(value: unknown): value is CustomerTestQuote {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const q = value as Record<string, unknown>;
  const identifier = (id: unknown) => typeof id === 'string' && id.length === 36
    && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(id);
  return identifier(q.quote_id) && identifier(q.quote_root_id) && identifier(q.order_id)
    && Number.isSafeInteger(q.quote_version) && Number(q.quote_version) > 0
    && Number.isSafeInteger(q.order_version) && Number(q.order_version) > 0
    && Number.isSafeInteger(q.amount_minor) && Number(q.amount_minor) > 0
    && typeof q.currency_code === 'string' && /^[A-Z]{3}$/.test(q.currency_code)
    && (q.state === 'confirmed' || q.state === 'approved') && q.environment === 'test' && typeof q.created === 'boolean';
}
async function loadReadableQuotes(): Promise<CustomerTestQuote[]> {
  const quotes = await loadCustomerTestQuotes();
  if (!quotes.every(readableQuote) || new Set(quotes.map(q => q.quote_id)).size !== quotes.length) {
    throw new Error('Test quote readback mismatch.');
  }
  return quotes;
}

export function CustomerTestCheckout({onSubmitted}: {onSubmitted?: () => void} = {}) {
  const [quotes, setQuotes] = useState<CustomerTestQuote[] | null>(null);
  const [status, setStatus] = useState('');
  const [pending, setPending] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const busy = useRef(true);
  useEffect(() => {
    let active = true;
    busy.current = true; setPending(true); setQuotes(null); setStatus('Loading test quote…');
    loadReadableQuotes().then(next => {
      if (active) {setQuotes(next); setStatus('');}
    }).catch(() => {if (active) setStatus('Test quote details are not available. Reload test quotes.');})
      .finally(() => {if (active) {busy.current = false; setPending(false);}});
    return () => {active = false;};
  }, [reloadKey]);

  async function change(quote: CustomerTestQuote, submit: boolean) {
    if (busy.current || quotes === null) return;
    busy.current = true; setPending(true); setStatus('');
    // The known payment/approval outcome must survive a failed refresh below -- the catch block
    // reports on this, not on whether the request merely completed, so a declined payment is never
    // mistaken for "the submission was saved" just because a decline is also a saved, real result.
    let outcome: 'submitted' | 'declined' | 'approved' | null = null;
    try {
      if (submit) {
        const payment = await authorizeCustomerMockPayment(quote);
        if (!payment || typeof payment !== 'object' || Array.isArray(payment)
          || typeof payment.payment_attempt_id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(payment.payment_attempt_id)
          || payment.provider !== 'deterministic_mock' || payment.environment !== 'test'
          || !['authorized','declined'].includes(payment.state) || payment.submitted !== (payment.state === 'authorized')
          || !('order_id' in payment) || payment.order_id !== quote.order_id
          || !('quote_id' in payment) || payment.quote_id !== quote.quote_id
          || !('quote_version' in payment) || payment.quote_version !== quote.quote_version) throw new Error('Mock authorization receipt missing');
        outcome = payment.state === 'authorized' ? 'submitted' : 'declined';
        if (payment.state === 'authorized') {
          onSubmitted?.();
          setQuotes(await loadReadableQuotes());
          setStatus('Deterministic mock authorization recorded. The order was submitted with no real charge.');
        } else {
          setQuotes(await loadReadableQuotes());
          setStatus('This test amount was deterministically declined (no real charge occurred). The order was not submitted; approve a new test quote for a different amount to retry.');
        }
        return;
      }
      const approved = await approveCustomerTestQuote(quote);
      if (!approved || typeof approved !== 'object' || Array.isArray(approved)
        || typeof approved.quote_id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(approved.quote_id)
        || approved.quote_id === quote.quote_id || approved.quote_root_id !== quote.quote_root_id
        || !Number.isSafeInteger(approved.quote_version) || approved.quote_version !== quote.quote_version + 1
        || approved.order_id !== quote.order_id || approved.order_version !== quote.order_version
        || approved.amount_minor !== quote.amount_minor || approved.currency_code !== quote.currency_code
        || approved.state !== 'approved' || approved.environment !== 'test' || typeof approved.created !== 'boolean') throw new Error('Test quote approval receipt missing');
      outcome = 'approved';
      setQuotes(await loadReadableQuotes());
      setStatus('You approved the exact test-only quote. No payment or charge occurred.');
    } catch (error) {
      setQuotes(null);
      if (outcome === 'submitted') setStatus('The mock submission was saved, but current test quotes could not be loaded. Reload test quotes to see the saved result. No real charge occurred.');
      else if (outcome === 'declined') setStatus('The decline was recorded and the order was not submitted, but current test quotes could not be loaded. Reload test quotes to see the current state. No real charge occurred.');
      else if (outcome === 'approved') setStatus('Your test quote approval was saved, but current test quotes could not be loaded. Reload test quotes to see the saved result. No real charge occurred.');
      else if (error instanceof ApiError && error.status === 403) setStatus('This test quote is not available. Reload test quotes.');
      else if (error instanceof ApiError && error.status === 409) setStatus('The test quote changed. Reload test quotes before taking another action.');
      else setStatus('The result could not be confirmed. Reload test quotes and check order progress before taking another action.');
    } finally {busy.current = false; setPending(false);}
  }

  return <section className="panel" aria-labelledby="test-checkout-title">
    <p className="eyebrow">TEST-ONLY CHECKOUT</p>
    <h2 id="test-checkout-title">Review your transparent test quote</h2>
    <p>No production price is set. This screen uses deterministic mock authorization only; no card, provider, or real charge is involved.</p>
    <p role="status" className="notice" aria-live="polite">{status || (quotes === null ? 'Loading test quote…' : quotes.length === 0 ? 'No current test quote requires action.' : '')}</p>
    <button type="button" disabled={pending} onClick={() => setReloadKey(current => current + 1)}>Reload test quotes</button>
    {quotes && quotes.length > 0 && <ol className="intake-list">{quotes.map(q => <li className="intake-card" key={q.quote_id}>
      <h3>{money(q.amount_minor, q.currency_code)} {q.currency_code}</h3>
      <p>Order {q.order_id} · test quote version {q.quote_version} · {q.state}.</p>
      {q.state === 'confirmed' ? <button disabled={pending} onClick={() => void change(q, false)}>Approve exact test quote</button>
        : <button disabled={pending} onClick={() => void change(q, true)}>Authorize deterministic mock and submit</button>}
    </li>)}</ol>}
  </section>;
}
